import { LazyLoading } from "@/controls/LazyLoading";
import { ExcelButton } from "@/controls/buttons/Excel-button";
import {
	dxtToLocalServerTime,
	dxtToUTC,
	getMomentTz,
} from "@dexteel/mesf-core";
import { Button, Grid, Paper, makeStyles } from "@material-ui/core";
import FormatListBulletedSharpIcon from "@material-ui/icons/FormatListBulletedSharp";
import {
	CellKeyDownEvent,
	ColDef,
	DefaultMenuItem,
	EditableCallbackParams,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
	RowClickedEvent,
	RowDoubleClickedEvent,
} from "ag-grid-community";

import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { useLogbookContext } from "../context/logbookContext";
import { Filters } from "./filters/filters";

const useStyles = makeStyles((theme) => ({
	container: {
		padding: theme.spacing(0, 2.5),
	},
	gridContainer: {
		width: "35%",
		height: "100%",
		borderRadius: "0.75rem",
		marginRight: 30,
		marginTop: 10,
		display: "flex",
		flexDirection: "column",
	},
	agGridContainer: {
		position: "relative",
		flex: 1,
	},
	btnExcel: {
		position: "absolute",
		bottom: 12,
		right: 15,
	},
	outletContainer: {
		width: "60%",
		height: "100%",
	},
}));
const moment = getMomentTz();
export const Logbook = ({
	searchEntries,
	isLoading,
}: {
	searchEntries: Function;
	isLoading: boolean;
}) => {
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const classes = useStyles();
	const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);

	const params = useParams();
	const navigate = useNavigate();
	const [columnDefs, setColumnDefs] = useState<ColDef[]>([]);
	const [showRemoveEntryDialog, setShowRemoveEntryDialog] = useState(false);
	const { EntryId } = useParams();

	const {
		state: { EntriesList, loadingTable, exportExcel },
		actions: {
			setEntrySelectedInTable,
			setEntryTitleToDelete,
			setEntryIdToDelete,
			setIsEditing,
			setExportExcel,
		},
	} = useLogbookContext();

	const defaultColDef = useMemo<ColDef>(() => {
		return {
			sortable: true,
			wrapText: true,
			autoHeight: true,
			wrapHeaderText: true,
			flex: 1,
		};
	}, []);

	const icons = useMemo(() => {
		return {
			filter: '<i class="fas fa-bars"></i>',
		};
	}, []);

	const onActionsClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		params: EditableCallbackParams,
	) => {
		(params.api as any).contextMenuFactory.showMenu(
			params.node,
			params.column,
			undefined,
			e,
		);
	};

	const rowClicked = (event: RowClickedEvent) => {
		setEntrySelectedInTable(event?.data);
		setIsEditing(false);
		navigate(`${event.data?.EntryId}${location.search}`);
	};

	const onCellKeyDown = useCallback(
		(event: CellKeyDownEvent) => {
			const { api, node, event: keyEvent } = event;
			if (!keyEvent) return;
			const key = (keyEvent as KeyboardEvent).key;

			if (key === "ArrowUp" || key === "ArrowDown") {
				keyEvent.preventDefault();
				const currentIndex = node.rowIndex !== null ? node.rowIndex : -1;
				const nextRowIndex =
					key === "ArrowUp" ? currentIndex - 1 : currentIndex + 1;
				const nextNode = api.getDisplayedRowAtIndex(nextRowIndex);

				if (nextNode) {
					nextNode.setSelected(true);
					api.ensureIndexVisible(nextRowIndex);
					setEntrySelectedInTable(nextNode.data);
					navigate(`${nextNode.data?.EntryId}${location.search}`);
				}
			}
		},
		[navigate, setEntrySelectedInTable],
	);

	const getLogbookContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data: any = params.node?.data;
			const result: (DefaultMenuItem | MenuItemDef)[] = [];

			if (data) {
			}
			if (!data) {
				result.push({
					name: "Export to Excel",
					action: customExcelExport,
					icon: '<i class="fas fa-file-excel"></i>',
				});
			}

			return result;
		},
		[gridApi],
	);

	useEffect(() => {
		const newColumns: ColDef[] = [];
		newColumns.push({
			field: "CreateTimestamp",
			headerName: "Created",
			minWidth: 138,
			flex: 1,
			valueGetter: (params) =>
				dxtToLocalServerTime(
					`${params.data.CreateTimestamp}`,
					"MM/dd/yy HH:mm",
				),
		}),
			newColumns.push({
				field: "AssetName",
				headerName: "Asset",
				sortable: true,
				flex: 1,
				minWidth: 70,
			}),
			newColumns.push({
				field: "Title",
				headerName: "Title",
				sortable: false,
				flex: 7,
				minWidth: 160,
			}),
			newColumns.push({
				field: "Author",
				headerName: "Created By",
				sortable: false,
				flex: 1,
				minWidth: 115,
			}),
			newColumns.push({
				field: "Content",
				headerName: "Content",
				hide: true,

				suppressColumnsToolPanel: true,
				suppressFiltersToolPanel: true,
				suppressHeaderMenuButton: true,
				suppressSizeToFit: true,
				valueGetter: (params) => params.data.Content,
			}),
			setColumnDefs(newColumns);
	}, [EntryId]);

	useEffect(() => {
		if (isLoading || loadingTable) {
			gridApi?.showLoadingOverlay();
		} else gridApi?.hideOverlay();
	}, [isLoading, gridApi, loadingTable]);

	const customExcelExport = useCallback(() => {
		const params = {
			fileName: "Logbook_Export.xlsx",
			columnKeys: [
				"CreateTimestamp",
				"Shift",
				"Crew",
				"Title",
				"CreatedBy",
				"Author",
				"Content",
			],
			processCellCallback: (params: any) => {
				if (params.column.colId === "CreateTimestamp") {
					return moment(params.value).format("MM/DD/yy - HH:mm");
				}
				return params.value;
			},
		};
		gridApi?.exportDataAsExcel(params);
	}, [gridApi]);

	useEffect(() => {
		if (exportExcel) {
			customExcelExport();
			setExportExcel(false);
		}
	}, [exportExcel, customExcelExport, setExportExcel]);

	return (
		<Grid container spacing={1}>
			<Grid item xs={12} md={5} container spacing={1}>
				<Grid item xs={12}>
					<Filters />
				</Grid>
				<Grid item xs={12}>
					<Paper elevation={1}>
						<div
							style={{
								height: "65vh",
								padding: "5px 0 60px",
								position: "relative",
							}}
						>
							<div style={gridStyle}>
								<AgGridReact
									rowData={EntriesList}
									columnDefs={columnDefs}
									loadingOverlayComponent={true}
									animateRows={true}
									getContextMenuItems={(e: any) =>
										getLogbookContextMenuItems(e)
									}
									icons={icons}
									onGridReady={(params) => setGridApi(params.api)}
									onRowClicked={rowClicked}
									onCellKeyDown={onCellKeyDown}
									getRowId={(params) => params.data.EntryId}
								/>
							</div>
							<div className={classes.btnExcel}>
								<ExcelButton onClick={() => setExportExcel(!exportExcel)} />
							</div>
						</div>
					</Paper>
				</Grid>
			</Grid>

			<Grid item xs={12} md={7}>
				<Outlet key={`entry-${params?.EntryId}`} />
			</Grid>
		</Grid>
	);
};
